import { inject, injectable } from 'inversify';
import {
  assertNotUndefinedOrNull,
  type Logger,
  TYPES,
} from '@bizzdesign/sdk-internal';
import { type LowLevelClient } from '@bizzdesign/sdk-lowlevel';
import { type Option, Some, None } from 'ts-results';
import {
  type AppClient,
  type CreateAppClientPayload,
  type CreateAppClientResult,
  type CreateGroupPayload,
  type CreateIdentityProviderPayload,
  type CreateUserPayload,
  type Group,
  type GroupMember,
  type GroupMemberResult,
  type GroupsExportRequest,
  type GroupsPageRequest,
  type GroupsPageResult,
  type IdentityProvider,
  type SetTemporaryPassword,
  type UpdateAppClientPayload,
  type UpdateGroupPayload,
  type UpdateIdentityProviderPayload,
  type UpdateUserPayload,
  type User,
  type UserManagementClientInterface,
  type UsersExportRequest,
  type UsersPageRequest,
  type UsersPageResult,
} from './interfaces';
import {
  mapAppClient,
  mapAppClientFromUpdate,
  mapCreateAppClientResult,
  mapGroup,
  mapGroupMember,
  mapGroupMemberResult,
  mapGroupWithoutMembers,
  mapIdentityProvider,
  mapIdentityProviderSimple,
  mapSetTemporaryPassword,
  mapUser,
} from './mappers/user-management.mapper.util';

@injectable()
export class UserManagementClientImpl implements UserManagementClientInterface {
  constructor(
    @inject(TYPES.Logger) private readonly logger: Logger,
    @inject(TYPES.LowLevelClient)
    private readonly lowLevelClient: LowLevelClient,
  ) {}

  // ==================== User Operations ====================

  async getUsers(): Promise<User[]> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching all users',
    });

    const result = await this.lowLevelClient.query({
      users: {
        id: true,
        email: true,
        givenName: true,
        familyName: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        origin: true,
        memberships: {
          groupId: true,
          groupName: true,
        },
        createdAt: true,
        updatedAt: true,
        lastActiveAt: true,
        firstLoginAt: true,
        trial: true,
      },
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetched users',
      payload: { count: result.users.length },
    });
    return result.users.map((user) => mapUser(user)) satisfies User[];
  }

  async getUsersPage(request: UsersPageRequest = {}): Promise<UsersPageResult> {
    const { page, sort, search } = request;
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching users page',
      payload: { page, sort, search },
    });

    const result = await this.lowLevelClient.query({
      usersPage: {
        __args: {
          ...(page ? { page: { offset: page.offset, limit: page.limit } } : {}),
          ...(sort ? { sort: { field: sort.field, order: sort.order } } : {}),
          ...(search !== undefined && search !== null ? { search } : {}),
        },
        users: {
          id: true,
          email: true,
          givenName: true,
          familyName: true,
          roles: {
            isAdmin: true,
            isSysAdmin: true,
            isPro: true,
            isCore: true,
            isBase: true,
          },
          origin: true,
          memberships: {
            groupId: true,
            groupName: true,
          },
          createdAt: true,
          updatedAt: true,
          lastActiveAt: true,
          firstLoginAt: true,
          trial: true,
        },
        totalCount: true,
        offset: true,
        limit: true,
      },
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetched users page',
      payload: {
        count: result.usersPage.users.length,
        totalCount: result.usersPage.totalCount,
      },
    });

    return {
      users: result.usersPage.users.map((user) => mapUser(user)),
      totalCount: result.usersPage.totalCount,
      offset: result.usersPage.offset,
      limit: result.usersPage.limit,
    } satisfies UsersPageResult;
  }

  async exportUsersCsv(request: UsersExportRequest = {}): Promise<string> {
    const { sort, search } = request;
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Exporting users CSV',
      payload: { sort, search },
    });

    const result = await this.lowLevelClient.query({
      exportUsers: {
        __args: {
          ...(sort ? { sort: { field: sort.field, order: sort.order } } : {}),
          ...(search !== undefined && search !== null ? { search } : {}),
        },
      },
    });

    return result.exportUsers satisfies string;
  }

  async getUser(userId: string): Promise<Option<User>> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching user',
      payload: { userId },
    });

    const result = await this.lowLevelClient.query({
      user: {
        __args: { userId },
        id: true,
        email: true,
        givenName: true,
        familyName: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        origin: true,
        memberships: {
          groupId: true,
          groupName: true,
        },
        createdAt: true,
        updatedAt: true,
        lastActiveAt: true,
        firstLoginAt: true,
        trial: true,
      },
    });

    if (result.user == null) {
      return None;
    }

    return Some(mapUser(result.user)) satisfies Option<User>;
  }

  async createUser(payload: CreateUserPayload): Promise<User> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Creating user',
    });

    const result = await this.lowLevelClient.mutation({
      createUser: {
        __args: { user: payload },
        id: true,
        email: true,
        givenName: true,
        familyName: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        origin: true,
        createdAt: true,
        updatedAt: true,
        trial: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Created user',
      payload: { userId: result.createUser.id },
    });

    return mapUser({
      ...result.createUser,
      roles: result.createUser.roles,
      lastActiveAt: null,
      firstLoginAt: null,
      memberships: [],
    }) satisfies User;
  }

  async updateUser(payload: UpdateUserPayload): Promise<User> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updating user',
      payload: { userId: payload.id },
    });

    const result = await this.lowLevelClient.mutation({
      updateUser: {
        __args: { user: payload },
        id: true,
        email: true,
        givenName: true,
        familyName: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        origin: true,
        createdAt: true,
        updatedAt: true,
        trial: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updated user',
      payload: { userId: result.updateUser.id },
    });

    return mapUser({
      ...result.updateUser,
      roles: result.updateUser.roles,
      lastActiveAt: null,
      firstLoginAt: null,
      memberships: [],
    }) satisfies User;
  }

  async deleteUser(userId: string): Promise<void> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleting user',
      payload: { userId },
    });

    await this.lowLevelClient.mutation({
      deleteUser: {
        __args: { userId },
        userId: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleted user',
      payload: { userId },
    });
  }

  async resendUserInvitation(userId: string): Promise<User> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Resending user invitation',
      payload: { userId },
    });

    const result = await this.lowLevelClient.mutation({
      resendUserInvitation: {
        __args: { userId: userId },
        id: true,
        email: true,
        givenName: true,
        familyName: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        origin: true,
        memberships: {
          groupId: true,
          groupName: true,
        },
        createdAt: true,
        updatedAt: true,
        lastActiveAt: true,
        firstLoginAt: true,
        trial: true,
      },
      __typename: true,
    });

    return mapUser(result.resendUserInvitation) satisfies User;
  }

  async setUserTemporaryPassword(
    userId: string,
  ): Promise<SetTemporaryPassword> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Setting user temporary password',
      payload: { userId },
    });

    const result = await this.lowLevelClient.mutation({
      setUserTemporaryPassword: {
        __args: { userId },
        temporaryPassword: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Set user temporary password',
      payload: { userId },
    });

    return mapSetTemporaryPassword(
      result.setUserTemporaryPassword,
    ) satisfies SetTemporaryPassword;
  }

  // ==================== Group Operations ====================

  async getGroups(): Promise<Group[]> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching all groups',
    });

    const result = await this.lowLevelClient.query({
      groups: {
        id: true,
        name: true,
        description: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        members: {
          userId: true,
          userEmail: true,
          userFullName: true,
        },
        createdAt: true,
        updatedAt: true,
      },
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetched groups',
      payload: { count: result.groups.length },
    });

    return result.groups.map((group) => mapGroup(group)) satisfies Group[];
  }

  async getGroupsPage(
    request: GroupsPageRequest = {},
  ): Promise<GroupsPageResult> {
    const { page, sort, search } = request;
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching groups page',
      payload: { page, sort, search },
    });

    const result = await this.lowLevelClient.query({
      groupsPage: {
        __args: {
          ...(page ? { page: { offset: page.offset, limit: page.limit } } : {}),
          ...(sort ? { sort: { field: sort.field, order: sort.order } } : {}),
          ...(search !== undefined && search !== null ? { search } : {}),
        },
        groups: {
          id: true,
          name: true,
          description: true,
          roles: {
            isAdmin: true,
            isSysAdmin: true,
            isPro: true,
            isCore: true,
            isBase: true,
          },
          members: {
            userId: true,
            userEmail: true,
            userFullName: true,
          },
          createdAt: true,
          updatedAt: true,
        },
        totalCount: true,
        offset: true,
        limit: true,
      },
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetched groups page',
      payload: {
        count: result.groupsPage.groups.length,
        totalCount: result.groupsPage.totalCount,
      },
    });

    return {
      groups: result.groupsPage.groups.map((group) => mapGroup(group)),
      totalCount: result.groupsPage.totalCount,
      offset: result.groupsPage.offset,
      limit: result.groupsPage.limit,
    } satisfies GroupsPageResult;
  }

  async exportGroupsCsv(request: GroupsExportRequest = {}): Promise<string> {
    const { sort, search } = request;
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Exporting groups CSV',
      payload: { sort, search },
    });

    const result = await this.lowLevelClient.query({
      exportGroups: {
        __args: {
          ...(sort ? { sort: { field: sort.field, order: sort.order } } : {}),
          ...(search !== undefined && search !== null ? { search } : {}),
        },
      },
    });

    return result.exportGroups satisfies string;
  }

  async getGroup(groupId: string): Promise<Option<Group>> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching group',
      payload: { groupId },
    });

    const result = await this.lowLevelClient.query({
      group: {
        __args: { groupId },
        id: true,
        name: true,
        description: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        members: {
          userId: true,
          userEmail: true,
          userFullName: true,
        },
        createdAt: true,
        updatedAt: true,
      },
    });

    if (result.group == null) {
      return None;
    }
    return Some(mapGroup(result.group)) satisfies Option<Group>;
  }

  async createGroup(payload: CreateGroupPayload): Promise<Group> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Creating group',
      payload: { name: payload.name },
    });

    const result = await this.lowLevelClient.mutation({
      createGroup: {
        __args: { group: payload },
        id: true,
        name: true,
        description: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        createdAt: true,
        updatedAt: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Created group',
      payload: { groupId: result.createGroup.id },
    });

    return mapGroupWithoutMembers(result.createGroup) satisfies Group;
  }

  async updateGroup(payload: UpdateGroupPayload): Promise<Group> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updating group',
      payload: { groupId: payload.id },
    });

    const result = await this.lowLevelClient.mutation({
      updateGroup: {
        __args: { group: payload },
        id: true,
        name: true,
        description: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        createdAt: true,
        updatedAt: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updated group',
      payload: { groupId: result.updateGroup.id },
    });

    return mapGroupWithoutMembers(result.updateGroup) satisfies Group;
  }

  async deleteGroup(groupId: string): Promise<void> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleting group',
      payload: { groupId },
    });

    await this.lowLevelClient.mutation({
      deleteGroup: {
        __args: { groupId },
        groupId: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleted group',
      payload: { groupId },
    });
  }

  async getGroupMembers(groupId: string): Promise<GroupMember[]> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching group members',
      payload: { groupId },
    });

    const result = await this.lowLevelClient.query({
      group: {
        __args: { groupId },
        members: {
          userId: true,
          userEmail: true,
          userFullName: true,
        },
      },
    });

    const members = result.group?.members ?? [];

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetched group members',
      payload: { groupId, count: members.length },
    });

    return members.map((member) =>
      mapGroupMember(member),
    ) satisfies GroupMember[];
  }

  async addGroupMember(
    groupId: string,
    userId: string,
  ): Promise<GroupMemberResult> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Adding group member',
      payload: { groupId, userId },
    });

    const result = await this.lowLevelClient.mutation({
      addGroupMember: {
        __args: { groupId, userId },
        groupId: true,
        userId: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Added group member',
      payload: { groupId, userId },
    });

    return mapGroupMemberResult(
      result.addGroupMember,
    ) satisfies GroupMemberResult;
  }

  async removeGroupMember(
    groupId: string,
    userId: string,
  ): Promise<GroupMemberResult> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Removing group member',
      payload: { groupId, userId },
    });

    const result = await this.lowLevelClient.mutation({
      removeGroupMember: {
        __args: { groupId, userId },
        groupId: true,
        userId: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Removed group member',
      payload: { groupId, userId },
    });

    return mapGroupMemberResult(
      result.removeGroupMember,
    ) satisfies GroupMemberResult;
  }

  // ==================== Identity Provider Operations ====================

  async getIdentityProviders(): Promise<IdentityProvider[]> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching all identity providers',
    });

    const result = await this.lowLevelClient.query({
      identityProviders: {
        providerName: true,
        providerType: true,
        state: true,
        createdAt: true,
        updatedAt: true,
      },
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetched identity providers',
      payload: { count: result.identityProviders.length },
    });

    return result.identityProviders.map((ip) =>
      mapIdentityProviderSimple(ip),
    ) satisfies IdentityProvider[];
  }

  async getIdentityProvider(
    providerName: string,
  ): Promise<Option<IdentityProvider>> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching identity provider',
      payload: { providerName },
    });

    const result = await this.lowLevelClient.query({
      identityProvider: {
        __args: { providerName },
        providerName: true,
        state: true,
        createdAt: true,
        updatedAt: true,
        attributeMappings: {
          emailAttribute: true,
          familyNameAttribute: true,
          givenNameAttribute: true,
        },
        providerDetailsOutput: {
          providerType: true,
          oidcDetailsOutput: {
            oidcIssuer: true,
            clientId: true,
          },
          samlDetailsOutput: {
            metaDataUrl: true,
          },
        },
      },
    });

    if (result.identityProvider == null) {
      return None;
    }
    return Some(
      mapIdentityProvider(result.identityProvider),
    ) satisfies Option<IdentityProvider>;
  }

  async createIdentityProvider(
    payload: CreateIdentityProviderPayload,
  ): Promise<IdentityProvider> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Creating identity provider',
      payload: { providerName: payload.providerName },
    });

    const result = await this.lowLevelClient.mutation({
      createIdentityProvider: {
        __args: { identityProvider: payload },
        providerName: true,
        state: true,
        createdAt: true,
        updatedAt: true,
        attributeMappings: {
          emailAttribute: true,
          familyNameAttribute: true,
          givenNameAttribute: true,
        },
        providerDetailsOutput: {
          providerType: true,
          oidcDetailsOutput: {
            oidcIssuer: true,
            clientId: true,
          },
          samlDetailsOutput: {
            metaDataUrl: true,
          },
        },
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Created identity provider',
      payload: { providerName: result.createIdentityProvider.providerName },
    });

    return mapIdentityProvider(
      result.createIdentityProvider,
    ) satisfies IdentityProvider;
  }

  async updateIdentityProvider(
    payload: UpdateIdentityProviderPayload,
  ): Promise<IdentityProvider> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updating identity provider',
      payload: { providerName: payload.providerName },
    });

    const result = await this.lowLevelClient.mutation({
      updateIdentityProvider: {
        __args: { identityProvider: payload },
        providerName: true,
        state: true,
        createdAt: true,
        updatedAt: true,
        attributeMappings: {
          emailAttribute: true,
          familyNameAttribute: true,
          givenNameAttribute: true,
        },
        providerDetailsOutput: {
          providerType: true,
          oidcDetailsOutput: {
            oidcIssuer: true,
            clientId: true,
          },
          samlDetailsOutput: {
            metaDataUrl: true,
          },
        },
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updated identity provider',
      payload: { providerName: result.updateIdentityProvider.providerName },
    });

    return mapIdentityProvider(
      result.updateIdentityProvider,
    ) satisfies IdentityProvider;
  }

  async deleteIdentityProvider(providerName: string): Promise<void> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleting identity provider',
      payload: { providerName },
    });

    await this.lowLevelClient.mutation({
      deleteIdentityProvider: {
        __args: { providerName },
        providerName: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleted identity provider',
      payload: { providerName },
    });
  }

  // ==================== App Client Operations ====================

  async getAppClients(): Promise<AppClient[]> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetching all app clients',
    });

    const result = await this.lowLevelClient.query({
      appClients: {
        appClientId: true,
        appClientName: true,
        authFlow: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        providerNames: true,
        accessTokenValidity: true,
        createdAt: true,
        updatedAt: true,
        callbackUrls: true,
        logoutUrls: true,
      },
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Fetched app clients',
      payload: { count: result.appClients.length },
    });

    return result.appClients.map((appClient) =>
      mapAppClient(appClient),
    ) satisfies AppClient[];
  }

  async createAppClient(
    payload: CreateAppClientPayload,
  ): Promise<CreateAppClientResult> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Creating app client',
      payload: { appClientName: payload.appClientName },
    });

    const result = await this.lowLevelClient.mutation({
      createAppClient: {
        __args: { appClient: payload },
        appClient: {
          appClientId: true,
          appClientName: true,
          authFlow: true,
          roles: {
            isAdmin: true,
            isSysAdmin: true,
            isPro: true,
            isCore: true,
            isBase: true,
          },
          providerNames: true,
          accessTokenValidity: true,
          createdAt: true,
          updatedAt: true,
          callbackUrls: true,
          logoutUrls: true,
        },
        appClientSecret: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Created app client',
      payload: { appClientId: result.createAppClient.appClient?.appClientId },
    });

    assertNotUndefinedOrNull(result.createAppClient.appClient);

    return mapCreateAppClientResult(
      result.createAppClient,
      mapAppClient(result.createAppClient.appClient),
    ) satisfies CreateAppClientResult;
  }

  async updateAppClient(payload: UpdateAppClientPayload): Promise<AppClient> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updating app client',
      payload: { appClientId: payload.appClientId },
    });

    const result = await this.lowLevelClient.mutation({
      updateAppClient: {
        __args: { appClient: payload },
        appClientId: true,
        appClientName: true,
        authFlow: true,
        roles: {
          isAdmin: true,
          isSysAdmin: true,
          isPro: true,
          isCore: true,
          isBase: true,
        },
        providerNames: true,
        accessTokenValidity: true,
        createdAt: true,
        updatedAt: true,
        callbackUrls: true,
        logoutUrls: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Updated app client',
      payload: { appClientId: result.updateAppClient.appClientId },
    });

    return mapAppClientFromUpdate(result.updateAppClient) satisfies AppClient;
  }

  async deleteAppClient(appClientId: string): Promise<void> {
    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleting app client',
      payload: { appClientId },
    });

    await this.lowLevelClient.mutation({
      deleteAppClient: {
        __args: { appClientId },
        appClientId: true,
      },
      __typename: true,
    });

    this.logger.debug({
      context: 'UserManagementClient',
      msg: 'Deleted app client',
      payload: { appClientId },
    });
  }
}
